import { useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from 'app/lib/axios';
import { channelKeys, listingKeys, loanKeys } from './keys';
import type { Loan } from 'app/types/loan';
import { useUser } from './user';
import { DynamicFeatureFlag, isFeatureEnabled } from 'app/utils/helpers';

export function useGuardianMutation() {
  const client = useQueryClient();
  const { data: currentUser, isPending: isUserLoading } = useUser();
  const canUseGuardian =
    !isUserLoading &&
    !!currentUser &&
    isFeatureEnabled(DynamicFeatureFlag.ListingGuardianship, currentUser.featureFlags);
  const mutation = useMutation({
    mutationFn: async (action: { loanId: string } | { listingId: string }) => {
      if (!canUseGuardian) throw new Error('Guardianship is not enabled for your account.');
      if ('loanId' in action) return (await http.post<Loan>(`/loans/${action.loanId}/guardian`)).data;
      await http.delete(`/loans/listings/${action.listingId}/guardian`);
      return null;
    },
    onSuccess: async (loan) => {
      if (loan) client.setQueryData(loanKeys.detail(loan.id), loan);
      await Promise.all(
        [listingKeys.all, loanKeys.all, channelKeys.all].map((queryKey) => client.invalidateQueries({ queryKey })),
      );
    },
  });
  return { ...mutation, canUseGuardian };
}
